/**
 * AbandonedCartAdmin – Configure and monitor abandoned cart email recovery.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Send, RefreshCw, Users, Mail, Clock, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AbandonedCartAdmin() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/api/admin/abandoned-cart-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/abandoned-carts`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSettings(sRes.data);
      setCarts(cRes.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/abandoned-cart-settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const triggerNow = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/api/cron/abandoned-cart`, {}, {
        headers: { Authorization: `Bearer ${process.env.REACT_APP_CRON_SECRET || ""}` }
      });
      toast.success("Triggered! Check email logs in a moment.");
      setTimeout(load, 2000);
    } catch {
      toast.error("Failed to trigger — secret mismatch or server error");
    } finally {
      setTriggering(false);
    }
  };

  const u = (patch) => setSettings(prev => ({ ...prev, ...patch }));

  if (!settings) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
    </div>
  );

  const totalItems = carts.reduce((s, c) => s + (c.items?.length || 0), 0);
  const totalEmails = carts.reduce((s, c) => s + (c.reminders_sent || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Abandoned Cart</h1>
            <p className="text-xs text-slate-400">Recover lost sales with automated recovery emails</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* KPI cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: "Abandoned Carts",   value: carts.length,  icon: ShoppingCart, color: "bg-orange-500" },
            { label: "Items in Carts",    value: totalItems,    icon: Users,        color: "bg-blue-500" },
            { label: "Recovery Emails Sent", value: totalEmails, icon: Mail,        color: "bg-green-500" },
            { label: "Carts Converted",   value: "—",           icon: Clock,        color: "bg-purple-500" },
          ].map(k => (
            <div key={k.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}>
                <k.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-black text-slate-800">{k.value}</p>
                <p className="text-xs text-slate-400">{k.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Settings card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-slate-700">Email Recovery Settings</h2>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-60">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Save Settings
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Enable toggle */}
            <div className="sm:col-span-2 flex items-center justify-between p-3 border border-slate-200 rounded-xl">
              <div>
                <p className="text-sm font-semibold text-slate-800">Enable Abandoned Cart Emails</p>
                <p className="text-xs text-slate-500 mt-0.5">Automatically email customers who leave items in their cart</p>
              </div>
              <button onClick={() => u({ enabled: !settings.enabled })}
                data-testid="abandoned-cart-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${settings.enabled ? "bg-orange-500" : "bg-slate-200"}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${settings.enabled ? "left-7" : "left-1"}`} />
              </button>
            </div>

            {/* Delay hours */}
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-2">First Email — Hours after cart abandoned</label>
              <div className="flex gap-2">
                {[1, 2, 4, 6, 12].map(h => (
                  <button key={h} onClick={() => u({ delay_hours: h })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${settings.delay_hours === h ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Currently: {settings.delay_hours} hour{settings.delay_hours !== 1 ? "s" : ""} after abandonment</p>
            </div>

            {/* Reminder hours */}
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-2">Second Email — Hours after first email</label>
              <div className="flex gap-2">
                {[12, 24, 48, 72].map(h => (
                  <button key={h} onClick={() => u({ reminder_hours: h })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${settings.reminder_hours === h ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Currently: {settings.reminder_hours} hours after first email</p>
            </div>

            {/* Max reminders */}
            <div>
              <label className="text-xs font-bold text-slate-600 block mb-2">Maximum Emails per Customer</label>
              <div className="flex gap-2">
                {[1, 2, 3].map(n => (
                  <button key={n} onClick={() => u({ max_reminders: n })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${settings.max_reminders === n ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Stop after {settings.max_reminders} email{settings.max_reminders !== 1 ? "s" : ""} per cart</p>
            </div>

            {/* Manual trigger */}
            <div className="flex items-end">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-2">Manual Trigger (Testing)</label>
                <button onClick={triggerNow} disabled={triggering}
                  data-testid="trigger-abandoned-cart-btn"
                  className="flex items-center gap-2 border border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-60">
                  <Play className="w-3.5 h-3.5" />
                  {triggering ? "Running…" : "Run Now"}
                </button>
                <p className="text-xs text-slate-400 mt-1">Triggers the cron job immediately for testing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Abandoned carts table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Active Abandoned Carts ({carts.length})</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <RefreshCw className="w-5 h-5 text-orange-500 animate-spin" />
            </div>
          ) : carts.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-sm text-slate-400">No abandoned carts — great news!</div>
          ) : (
            <>
              <div className="grid grid-cols-12 gap-3 px-5 py-2.5 bg-slate-50 text-[11px] font-bold text-slate-400 uppercase">
                <span className="col-span-3">Customer</span>
                <span className="col-span-2">Items</span>
                <span className="col-span-3">Last Updated</span>
                <span className="col-span-2">Emails Sent</span>
                <span className="col-span-2">Last Email</span>
              </div>
              {carts.map((c, i) => (
                <div key={c.id || i} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-t border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{c.customer_name || "—"}</p>
                    <p className="text-xs text-slate-400 truncate">{c.customer_email}</p>
                    {c.customer_phone && <p className="text-xs text-slate-400 truncate">{c.customer_phone}</p>}
                    {c.customer_address && <p className="text-[11px] text-slate-400 truncate" title={c.customer_address}>{c.customer_address}</p>}
                  </div>
                  <div className="col-span-2">
                    <div className="flex gap-1 flex-wrap">
                      {(c.items || []).slice(0, 3).map((item, j) => (
                        item.image
                          ? <img key={j} src={item.image} alt="" className="w-8 h-8 rounded object-cover" />
                          : <div key={j} className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-[10px] text-slate-400">{j+1}</div>
                      ))}
                      {c.items?.length > 3 && <span className="text-xs text-slate-400 self-center">+{c.items.length - 3}</span>}
                    </div>
                  </div>
                  <p className="col-span-3 text-xs text-slate-500">{fmtDate(c.updated_at)}</p>
                  <p className="col-span-2 text-xs font-bold text-orange-600">{c.reminders_sent || 0}</p>
                  <p className="col-span-2 text-xs text-slate-500">{fmtDate(c.last_reminder_at)}</p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
